import {
    ApolloClient,
    ApolloLink,
    HttpLink,
    InMemoryCache,
} from '@apollo/client';
import { setContext } from '@apollo/client/link/context';
import { onError } from '@apollo/client/link/error';
import i18n from 'i18next';

import { getIntlPath } from 'common/utils';
import { useAuth } from 'auth/hooks';
import { tokenIsValid, expirationDateIsInFuture } from 'auth/utils';

import { IRefresh } from './types';

const httpLink = new HttpLink({
    uri: process.env.REACT_APP_GRAPHQL_URL,
    credentials: 'include',
    headers: {
        lng: i18n.language,
    },
});

// Link to refresh the accessToken when it has expired
const tokenRefreshLink = setContext(async (_request, context) => {
    if (!process.env.REACT_APP_GRAPHQL_URL) {
        throw new Error('Graphql API endpoint not set');
    }

    const { getAccessToken, login, isLoggedIn } = useAuth();

    const accessToken = getAccessToken();

    // No need to refresh in this case
    if (!isLoggedIn() || tokenIsValid(accessToken)) return context;

    try {
        const refreshExpiry = localStorage.getItem('refreshExpiresIn');

        if (!refreshExpiry || !expirationDateIsInFuture(refreshExpiry)) {
            throw new Error('No valid refresh token expiry set');
        }

        const response = await fetch(process.env.REACT_APP_GRAPHQL_URL, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
            },
            credentials: 'include',
            body: JSON.stringify({
                query: `mutation {
                    refreshToken {
                        token
                        refreshToken
                        refreshExpiresIn
                    }
                }`,
            }),
        });

        const jsonResponse = await response.json();

        if (jsonResponse.errors) {
            throw new Error('No refresh token in response');
        }

        const {
            token,
            refreshExpiresIn,
        }: IRefresh = jsonResponse.data.refreshToken;

        // The login function performs the same steps as setting a new access
        // token using a refresh token
        login(token, refreshExpiresIn);

        return context;
    } catch {
        // If refresh failed, redirect to login screen
        window.location.href = getIntlPath('login');

        return context;
    }
});

const authLink = setContext((_request, context) => {
    const accessToken = useAuth().getAccessToken();

    if (accessToken) {
        context.headers = context.headers ?? {};
        context.headers.Authorization = `JWT ${accessToken}`;
    }

    return context;
});

const errorLink = onError(({ networkError }) => {
    if (networkError) {
        networkError.message = i18n.t('messages.error');
    }
});

export const client = new ApolloClient({
    link: ApolloLink.from([errorLink, tokenRefreshLink, authLink, httpLink]),
    cache: new InMemoryCache(),
});
